import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { SERVICE_STATUSES, DEFAULT_SERVICE_STATUSES } from "../lib/serviceCallStatuses";
import { toast } from "sonner";
import BACKEND_URL from "../lib/apiConfig";
import { axiosWithRetry } from "../lib/axiosRetry";
import { describeSync } from "../lib/syncToast";

const API = `${BACKEND_URL}/api`;

// Calculate age in days from a date string
export const calculateAge = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  } catch {
    return '-';
  }
};

// Format date as "M-D-YY"
export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = String(date.getFullYear()).slice(-2);
    return `${month}-${day}-${year}`;
  } catch {
    return dateStr;
  }
};

// iter 370do — A service call is "currently paused" when paused_until
// is set (regardless of paused_at). Used to gate visibility on the
// dispatch view + exclude from average-age stats.
//
// iter 370dw — Waiting-on-Parts calls *also* set paused_at/paused_until
// (so age math freezes correctly), but they live in their own bucket
// above Unscheduled — NOT in the bottom Paused section. We therefore
// exclude them here so `isPaused` only matches plain-old pauses.
export const isPaused = (call) => {
  if (!call) return false;
  if (call.waiting_on_parts) return false;
  return !!(call.paused_until && String(call.paused_until).trim());
};

// iter 370dw — Waiting on Parts predicate. Used to bucket the call
// into the dedicated section between Scheduled and Unscheduled, and
// to drive the map-hiding heuristic.
export const isWaitingOnParts = (call) => {
  if (!call) return false;
  return !!call.waiting_on_parts;
};

// iter 370dw — Returns true if a waiting-on-parts call should be
// visible on the map. Visible when parts_eta is within ~7 days
// (or already past). Hidden when ETA is further out than that.
// Calls without a parts_eta are always hidden (no reason to clutter
// the map with stops that aren't actionable yet).
export const isWaitingOnPartsVisibleOnMap = (call, _today = new Date()) => {
  if (!isWaitingOnParts(call)) return true;  // not waiting → no opinion
  const eta = call.parts_eta || call.paused_until;
  if (!eta) return false;
  const etaDate = new Date(`${eta}T12:00:00`);
  if (Number.isNaN(etaDate.getTime())) return false;
  const diffDays = Math.ceil((etaDate - _today) / 86400000);
  return diffDays <= 7;  // shows when ETA is within 7 days or in the past
};

// iter 370do — Returns the ticket's age in days, but freezes counting
// while the ticket is paused AND retroactively subtracts every day the
// ticket spent in prior pause cycles. Replaces the simple
// `now - created_date` math which kept ticking through pauses.
//
// Inputs:
//   call — full service call doc; we need created_date, paused_at,
//          paused_until, paused_days.
// Output:
//   integer days, or '-' if no created date.
export const calculateEffectiveAge = (call) => {
  if (!call) return '-';
  const createdStr = call.created_date || call.created || call.created_at;
  if (!createdStr) return '-';
  try {
    const created = new Date(createdStr);
    const now = new Date();
    const totalDays = Math.floor((now - created) / 86400000);

    // Past-pause cycles already accumulated server-side.
    let pausedDays = Number(call.paused_days || 0);

    // If currently paused, add the in-flight pause duration so the age
    // FREEZES at the moment of pause rather than ticking through it.
    // iter 372e — check paused_until directly (NOT isPaused) so
    // waiting-on-parts calls, which also set paused_at/paused_until,
    // freeze too. isPaused() deliberately excludes them for bucketing.
    if (call.paused_until && String(call.paused_until).trim() && call.paused_at) {
      try {
        const pausedAt = new Date(call.paused_at);
        if (!isNaN(pausedAt.getTime())) {
          const sinceDays = Math.floor((now - pausedAt) / 86400000);
          if (sinceDays > 0) pausedDays += sinceDays;
        }
      } catch { /* ignore parse errors */ }
    }

    return Math.max(0, totalDays - pausedDays);
  } catch {
    return '-';
  }
};

// Format scheduled date for rows (iter 373e — Jake):
//   today     → "Today, July 29th"
//   tomorrow  → "Tomorrow, July 30th"
//   otherwise → "Friday, July 31st" (year appended only when different)
export const formatScheduledDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const ordinal = (n) => {
      const v = n % 100;
      if (v >= 11 && v <= 13) return `${n}th`;
      return `${n}${['th', 'st', 'nd', 'rd'][Math.min(n % 10, 4)] || 'th'}`;
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((date - today) / 86400000);
    const lead = diffDays === 0 ? 'Today' : diffDays === 1 ? 'Tomorrow' : dayNames[date.getDay()];
    const yearSuffix = date.getFullYear() !== today.getFullYear() ? `, ${date.getFullYear()}` : '';
    return `${lead}, ${monthNames[date.getMonth()]} ${ordinal(date.getDate())}${yearSuffix}`;
  } catch {
    return dateStr;
  }
};

// Airtable status colors
export const getStatusColor = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('need to close') || s.includes('close out')) return '#FF3B30';
  if (s === 'in shop') return '#22D3EE';
  if (s === 'shop work incomplete') return '#FB923C';
  if (s.includes('complete') || s.includes('archive')) return '#39FF14';
  if (s.includes('priority')) return '#FF3B30';
  if (s.includes('waiting') || s.includes('ready')) return '#FFD60A';
  if (s.includes('standard')) return '#007AFF';
  if (s.includes('duplicate') || s.includes('delete')) return '#52525B';
  if (!status || s === '(no status)') return '#6B7280';
  return '#A1A1AA';
};

// Available Airtable statuses
export const AIRTABLE_STATUSES = SERVICE_STATUSES;

// Default status filter (excludes completed/archived)
export const DEFAULT_STATUS_FILTER = DEFAULT_SERVICE_STATUSES;

export function useServiceCalls() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  
  // Fetch service calls
  const fetchCalls = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/service-calls`);
      setCalls(res.data);
    } catch (err) {
      console.error("Failed to fetch service calls:", err);
      toast.error("Failed to load service calls");
    } finally {
      setLoading(false);
    }
  }, []);

  // Update a service call
  const updateCall = useCallback(async (callId, updates) => {
    try {
      await axios.patch(`${API}/service-calls/${callId}`, updates);
      setCalls(prev => prev.map(c => c.id === callId ? { ...c, ...updates } : c));
      return true;
    } catch (err) {
      console.error("Failed to update call:", err);
      toast.error("Failed to update service call");
      return false;
    }
  }, []);

  // Delete a service call
  const deleteCall = useCallback(async (callId) => {
    if (!window.confirm("Are you sure you want to delete this service call?")) return false;
    try {
      await axios.delete(`${API}/service-calls/${callId}`);
      setCalls(prev => prev.filter(c => c.id !== callId));
      toast.success("Service call deleted");
      return true;
    } catch (err) {
      console.error("Failed to delete call:", err);
      toast.error("Failed to delete service call");
      return false;
    }
  }, []);

  // Toggle date lock
  const toggleDateLock = useCallback(async (callId) => {
    const call = calls.find(c => c.id === callId);
    if (!call) return;
    
    const newLockedState = !call.date_locked;
    setCalls(prev => prev.map(c => 
      c.id === callId ? { ...c, date_locked: newLockedState } : c
    ));
    
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await axios.patch(`${API}/service-calls/${callId}`, { date_locked: newLockedState }, { timeout: 10000 });
        toast.success(newLockedState ? "Date locked" : "Date unlocked");
        return;
      } catch (err) {
        if (attempt < 2 && (!err.response || err.response?.status >= 500)) {
          await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
          continue;
        }
        console.error("Failed to toggle date lock:", err);
        toast.error("Failed to update lock state");
        setCalls(prev => prev.map(c => 
          c.id === callId ? { ...c, date_locked: !newLockedState } : c
        ));
      }
    }
  }, [calls]);

  // Update scheduled date
  const updateScheduledDate = useCallback(async (callId, newDate) => {
    const call = calls.find(c => c.id === callId);
    if (call?.date_locked) {
      toast.error("Date is locked. Unlock it first to edit.");
      return false;
    }
    
    setCalls(prev => prev.map(c => 
      c.id === callId ? { ...c, scheduled_date: newDate } : c
    ));
    
    try {
      await axiosWithRetry(
        () => axios.patch(`${API}/service-calls/${callId}`, { scheduled_date: newDate }, { timeout: 20000 }),
        { retries: 2, delay: 1200, maxDelay: 3000, silent: true }
      );
      toast.success("Scheduled date updated");
      return true;
    } catch (err) {
      console.error("Failed to update scheduled date:", err);
      const status = err?.response?.status;
      if (status === 504 || err?.code === 'ECONNABORTED') {
        toast.error("Server slow — date change not saved. Try again.");
      } else if (status >= 500) {
        toast.error(`Server error (${status}) saving date. Try again.`);
      } else {
        toast.error("Failed to update date");
      }
      await fetchCalls();
      return false;
    }
  }, [calls, fetchCalls]);

  // Update status
  const updateStatus = useCallback(async (callId, newStatus) => {
    const oldCall = calls.find(c => c.id === callId);
    
    setCalls(prev => prev.map(c => 
      c.id === callId ? { ...c, airtable_status: newStatus, _pendingSync: true } : c
    ));
    
    try {
      await axios.patch(`${API}/service-calls/${callId}`, { airtable_status: newStatus });
      setCalls(prev => prev.map(c => 
        c.id === callId ? { ...c, _pendingSync: false } : c
      ));
      
      if (oldCall?.airtable_id) {
        toast.success("Status updated & synced");
      } else {
        toast.success("Status updated");
      }
      return true;
    } catch (err) {
      console.error("Failed to update status:", err);
      toast.error("Failed to update status");
      setCalls(prev => prev.map(c => 
        c.id === callId ? { ...c, airtable_status: oldCall?.airtable_status, _pendingSync: false } : c
      ));
      return false;
    }
  }, [calls]);

  // Update billing
  const updateBilling = useCallback(async (callId, field, value) => {
    setCalls(prev => prev.map(c => 
      c.id === callId ? { ...c, [field]: value } : c
    ));
    
    try {
      await axios.patch(`${API}/service-calls/${callId}`, { [field]: value });
      toast.success("Billing info saved");
      return true;
    } catch (err) {
      console.error("Failed to update billing:", err);
      toast.error("Failed to save billing info");
      await fetchCalls();
      return false;
    }
  }, [fetchCalls]);

  // Update internal notes
  const updateInternalNotes = useCallback(async (callId, notes) => {
    setCalls(prev => prev.map(c => 
      c.id === callId ? { ...c, internal_notes: notes, latest_note: notes } : c
    ));
    
    try {
      await axios.patch(`${API}/service-calls/${callId}`, { internal_notes: notes });
      toast.success("Notes saved");
      return true;
    } catch (err) {
      console.error("Failed to save notes:", err);
      toast.error("Failed to save notes");
      await fetchCalls();
      return false;
    }
  }, [fetchCalls]);

  // Sync from Airtable
  const syncFromAirtable = useCallback(async (silent = false, useIncremental = true) => {
    try {
      setSyncing(true);
      const endpoint = useIncremental
        ? `${API}/integrations/airtable/sync-incremental${silent ? "" : "?force=1"}`
        : `${API}/integrations/airtable/sync-service-calls`;
      
      const res = await axios.post(endpoint);
      if (!silent) toast.success(describeSync(res.data));
      setLastSyncedAt(res.data.synced_at);
      await fetchCalls();
      return true;
    } catch (err) {
      console.error("Sync failed:", err);
      if (!silent) {
        toast.error("Failed to sync from Airtable");
      }
      return false;
    } finally {
      setSyncing(false);
    }
  }, [fetchCalls]);

  return {
    calls,
    setCalls,
    loading,
    syncing,
    lastSyncedAt,
    fetchCalls,
    updateCall,
    deleteCall,
    toggleDateLock,
    updateScheduledDate,
    updateStatus,
    updateBilling,
    updateInternalNotes,
    syncFromAirtable
  };
}

export default useServiceCalls;
